import {type InfiniteData, useInfiniteQuery} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import type {PageResponse} from "~/services/ApiTypes";
import {supplierRiskSignalsUrl} from "~/pages/supplier-360/services/supplier360Urls";
import type {SupplierRiskSignal, SupplierRiskSignalFilters} from "~/pages/supplier-360/types/Supplier360Types";

type RiskSignalPage = PageResponse<SupplierRiskSignal>;

/**
 * An entity's risk signals, one `page_size` page at a time, for infinite scroll
 * (`GET supplier_360/risk_signal/?entity={id}&…&page={n}`, §2.10). A new filter set starts a new list.
 */
export function useSupplierRiskSignalsInfiniteQuery(
    entityId: number | undefined,
    filters: SupplierRiskSignalFilters,
    pageSize: number,
) {
    // The URL without `page` identifies the list; each page's own URL adds it
    const listUrl = supplierRiskSignalsUrl(entityId ?? 0, {...filters, page: 1, pageSize});

    return useInfiniteQuery<RiskSignalPage, Error, InfiniteData<RiskSignalPage>, readonly [string], number>({
        queryKey: [listUrl] as const,
        queryFn: async ({pageParam, signal}) => {
            const url = supplierRiskSignalsUrl(entityId ?? 0, {...filters, page: pageParam, pageSize});
            return (await queryHttp.get<RiskSignalPage>(url, {signal})).data;
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => lastPage.next ? allPages.length + 1 : undefined,
        enabled: Boolean(entityId),
    });
}
